import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { TemplateState } from '../../../../types/common/types';
import { CompanyDetailsFormWebComponent } from '../../../common/companyDetailsForm';
import {
  WEB_COMPONENT_COMPANY_AUTHENTICATION_FORM,
  WEB_COMPONENT_COMPANY_DETAILS_FORM,
  WEB_COMPONENT_CUSTOM_FIELDS_FORM,
  WEB_COMPONENT_GZ_WIZARD,
  WEB_COMPONENT_LICENSE_MAIN_FORM,
} from '../../../../webcomponents.constants';
import { GzWizardWebComponent } from '../../../../ui/gzWizard';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { CompanyAuthenticationFormWebComponent } from '../../../common/authenticationSettingsForm';
import { LicenseMainFormWebComponent } from '../../../../license/licenseMainForm';
import { CustomFieldsFormWebComponent } from '../../../common/customFieldsForm';
import { CreateCompanyEvent } from '../types/CreateCompanyEvent';
import { CreateCompanyViewData } from '../types/CreateCompanyViewData';

export class CreateCompanyPanelView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;

    $wizard: GzWizardWebComponent;

    $companyDetailsFormWrapper: HTMLElement;
    $companyDetailsForm: CompanyDetailsFormWebComponent;

    $companyAuthenticationFormWrapper: HTMLElement;
    $companyAuthenticationForm: CompanyAuthenticationFormWebComponent;

    $companyLicenseMainFormWrapper: HTMLElement;
    $companyLicenseMainForm: LicenseMainFormWebComponent;
    $customFieldsForm: CustomFieldsFormWebComponent;

    $cancelButton: GzButtonWebComponent;
    $backButton: GzButtonWebComponent;
    $nextButton: GzButtonWebComponent;
  };

  private componentsStarted = false;

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),

      $companyDetailsFormWrapper: this.shadow.querySelector('#company-details-form-wrapper'),
      $wizard: this.shadow.querySelector(WEB_COMPONENT_GZ_WIZARD),
      $companyDetailsForm: this.shadow.querySelector(WEB_COMPONENT_COMPANY_DETAILS_FORM),

      $companyAuthenticationFormWrapper: this.shadow.querySelector('#company-authentication-form-wrapper'),
      $companyAuthenticationForm: this.shadow.querySelector(WEB_COMPONENT_COMPANY_AUTHENTICATION_FORM),

      $companyLicenseMainFormWrapper: this.shadow.querySelector('#company-add-license-form-wrapper'),
      $companyLicenseMainForm: this.shadow.querySelector(WEB_COMPONENT_LICENSE_MAIN_FORM),
      $customFieldsForm: this.shadow.querySelector(WEB_COMPONENT_CUSTOM_FIELDS_FORM),

      $cancelButton: this.shadow.querySelector('#cancel-button'),
      $backButton: this.shadow.querySelector('#back-button'),
      $nextButton: this.shadow.querySelector('#next-button'),
    };

    this.setLoadingState();

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$cancelButton.innerText = this.dependencies.translateFn('Cancel');
    this.dom.$backButton.innerText = this.dependencies.translateFn('Back');
    this.dom.$nextButton.innerText = this.dependencies.translateFn('Next');
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  buildSuccessView(viewData: CreateCompanyViewData) {
    this.setWizardOptions();

    this.dom.$companyDetailsForm.injectDependencies(this.dependencies);
    this.dom.$companyAuthenticationForm.injectDependencies(this.dependencies);
    this.dom.$companyLicenseMainForm.injectDependencies(this.dependencies);

    const startWebCompPromises: Promise<void>[] = [
      this.dom.$companyDetailsForm.start(),
      this.dom.$companyAuthenticationForm.start(),
      this.dom.$companyLicenseMainForm.start(),
    ];

    if (viewData.isCustomFieldsAvailable) {
      this.dom.$customFieldsForm.injectDependencies(this.dependencies);
      startWebCompPromises.push(this.dom.$customFieldsForm.start());
    }

    Promise.all(startWebCompPromises)
      .then(() => {
        this.removeLoadingState();
        this.componentsStarted = true;

        this.dom.$wrapper.dispatchEvent(
          new CustomEvent(CreateCompanyEvent.OnViewInit, {
            bubbles: true,
            composed: true,
          })
        );
      })
      .catch(console.error);
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private setWizardOptions() {
    this.dom.$wizard.options = [
      {
        step: 1,
        title: this.dependencies.translateFn('Company details'),
        message: '',
      },
      {
        step: 2,
        title: this.dependencies.translateFn('Authentication'),
        message: '',
      },
      {
        step: 3,
        title: this.dependencies.translateFn('Licensing'),
        message: '',
      },
    ];
  }

  public get hasUnsavedChanges(): boolean {
    if (!this.componentsStarted) {
      // can happen when the error view is shown or if user exits too fast
      return false;
    }

    // if any form has unsaved changes, return true
    return (
      this.dom.$companyDetailsForm.hasUnsavedChanges ||
      this.dom.$companyAuthenticationForm.hasUnsavedChanges ||
      this.dom.$companyLicenseMainForm.hasUnsavedChanges
    );
  }
}
